import { alpha, createTheme, getContrastRatio, Theme } from "@mui/material";
import { blue, grey } from "@mui/material/colors";
import { PaletteOptions } from "@mui/material/styles/createPalette";
import { ThemeOptions } from "@mui/material/styles/createTheme";
import { SpecificColors } from "../constants/ColorConstants";

declare module "@mui/material/styles" {
    interface Palette {
        purple: Palette["primary"];
        gold: Palette["primary"];
        silver: Palette["primary"];
    }

    interface PaletteOptions {
        purple?: PaletteOptions["primary"];
        gold?: PaletteOptions["primary"];
        silver?: PaletteOptions["primary"];
    }
}

declare module "@mui/material/Button" {
    interface ButtonPropsColorOverrides {
        purple: true;
        gold: true;
        silver: true;
    }
}

const theme = createTheme();

export function customizeTheme(mode: "light" | "dark"): Theme {
    let baseTheme: ThemeOptions = {
        direction: "rtl",
        typography: {
            fontFamily: "IranSans",
            fontSize: 12,
            // Set standard font size for each MUI typography variant
            h1: {
                fontSize: "2rem", // ~32px
                fontWeight: 700,
            },
            h2: {
                fontSize: "1.75rem", // ~28px
                fontWeight: 700,
            },
            h3: {
                fontSize: "1.5rem", // ~24px
                fontWeight: 700,
            },
            h4: {
                fontSize: "1.25rem", // ~20px
                fontWeight: 700,
            },
            h5: {
                fontSize: "1rem", // ~16px
                fontWeight: 700,
            },
            h6: {
                fontSize: "0.875rem", // 16px
                fontWeight: 700,
            },
            subtitle1: {
                fontSize: "0.75rem", // 16px
                fontWeight: 500,
            },
            subtitle2: {
                fontSize: "0.75rem", // 14px
                fontWeight: 500,
            },
            body1: {
                fontSize: "0.875rem", // 16px
                fontWeight: 400,
            },
            body2: {
                fontSize: "0.75rem", // 14px
                fontWeight: 400,
            },
            button: {
                fontSize: "0.75rem", // 14px
                fontWeight: 500,
                textTransform: "none",
            },
            caption: {
                fontSize: "0.625rem", // 12px
                fontWeight: 400,
            },
            overline: {
                fontSize: "0.625rem", // 12px
                fontWeight: 400,
                textTransform: "uppercase",
            },
        },
        components: {
            MuiPaper: {
                styleOverrides: {
                    root: {
                        boxShadow: "none",
                        border: "1px solid",
                        borderColor: mode === "light" ? "#DDDDDD" : "#444444",
                        borderRadius: "12px",
                    },
                },
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        boxShadow: "none",
                        borderRadius: "12px",
                    },
                },
            },
            MuiAccordion: {
                styleOverrides: {
                    root: {
                        "&::before": {
                            display: "none",
                        },
                        borderRadius: "12px !important",
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: "10px",
                    },
                },
            },
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        zIndex: 888,
                    },
                    colorPrimary: {
                        backgroundColor:
                            mode === "light" ? "#FFFFFF" : "#202025",
                        boxShadow: "none",
                        border: "none",
                        borderBottom: "1px solid #CCCCCC",
                        borderColor: mode === "light" ? "#DDDDDD" : "#444444",
                        borderRadius: 0,
                    },
                },
            },
            MuiMobileStepper: {
                styleOverrides: {
                    dot: {
                        backgroundColor: "rgba(255,255,255,0.4)",
                    },
                    dotActive: {
                        backgroundColor: "white",
                    },
                },
            },
            MuiStepLabel: {
                styleOverrides: {
                    label: {
                        fontSize: "16px",
                        [theme.breakpoints.down("md")]: {
                            fontSize: "12px",
                        },
                    },
                },
            },
            MuiCardHeader: {
                styleOverrides: {
                    root: {
                        backgroundColor:
                            mode === "light" ? grey[300] : grey[900],
                    },
                },
            },
            MuiCardActions: {
                styleOverrides: {
                    root: {
                        backgroundColor:
                            mode === "light" ? grey[300] : grey[900],
                    },
                },
            },
            MuiSwipeableDrawer: {
                defaultProps: {
                    sx: { borderRadius: 0 },
                },
            },
            MuiCssBaseline: {
                styleOverrides: `
                @font-face {
                  font-family: 'IranSans';
                  font-style: normal;
                  font-display: swap;
                  font-weight: 400;
                  src: url(./fonts/IranSans.woff2) format('woff2');
                }
                @font-face {
                  font-family: 'Material Icons';
                  font-style: normal;
                  font-weight: 400;
                  src: url('./fonts/GoogleFonts.woff2') format('woff2');
                }`,
            },
        },
    };

    let baseThemeOptions: PaletteOptions = {
        secondary: {
            main: "#009e44",
        },
        purple: {
            main: SpecificColors.Purple,
            light: alpha(SpecificColors.Purple, 0.5),
            dark: alpha(SpecificColors.Purple, 0.9),
            contrastText:
                getContrastRatio(SpecificColors.Purple, "#fff") > 4.5
                    ? "#fff"
                    : "#111",
        },
        silver: {
            main: SpecificColors.Silver,
            light: alpha(SpecificColors.Silver, 0.5),
            dark: alpha(SpecificColors.Silver, 0.9),
            contrastText:
                getContrastRatio(SpecificColors.Silver, "#fff") > 4.5
                    ? "#fff"
                    : "#111",
        },
        gold: {
            main: SpecificColors.Gold,
            light: alpha(SpecificColors.Gold, 0.5),
            dark: alpha(SpecificColors.Gold, 0.9),
            contrastText:
                getContrastRatio(SpecificColors.Gold, "#fff") > 4.5
                    ? "#fff"
                    : "#111",
        },
    };

    let lightThemePalette: PaletteOptions = {
        mode: "light",
        primary: blue,
        background: {
            default: "#EEEEEE",
            paper: "white",
        },
        text: {
            primary: grey[900],
            secondary: grey[700],
        },
        ...baseThemeOptions,
    };

    let darkThemePalette: PaletteOptions = {
        mode: "dark",
        primary: blue,
        background: {
            default: "#19191e",
            paper: "#202025",
        },
        text: {
            primary: grey[100],
            secondary: grey[300],
        },
        ...baseThemeOptions,
    };

    let options = {
        ...baseTheme,
        palette: mode === "light" ? lightThemePalette : darkThemePalette,
    };

    return createTheme(options);
}
